/**
 * Turns one event object from /event/list or /feed/upcoming into the exact
 * row the score row renders (Scores UX spec v1.0 section 1).
 *
 * Server resolves, client renders: the status text is the server's
 * `statusLabel`, the winner is `winnerTeamId` / `isTie`, live is `isLive` and
 * `status`. Nothing here computes a winner from scores or builds a clock
 * string. Choosing WHICH server name fits the cell is the row's layout
 * decision, made in `ScoreRow.tsx` through `pickTeamLabel`.
 */
import { resolvePlatePair } from "../gameDetail/teamPlateText";
import type { typeLiveStateWire } from "../liveState/liveStateTypes";

type typeScoresTeamInput = {
  teamId: string;
  name: string;
  /** The nickname ("Chiefs", "Red Sox") events-api sends for NFL, UFL, NBA, WNBA, MLB and NHL teams, null elsewhere (ISSUE-1197). */
  shortName?: string | null;
  abbreviation: string;
  score?: number | null;
  logoUrl?: string | null;
  primaryColorHex?: string | null;
  /** ISSUE-1388. The server's poll rank ("5", no "#") for a ranked college
   *  team, null or absent otherwise. */
  pollRankLabel?: string | null;
};

/** The event shape this module reads. A structural subset of the object
 *  /event/list and /feed/upcoming both return, naming only the fields used. */
export type typeScoresEventInput = {
  eventId: string;
  /** The event's league, which the Your teams card captions each row with. */
  leagueId?: string;
  status:
    "scheduled" | "inProgress" | "final" | "postponed" | "canceled" | "delayed";
  statusLabel: string;
  isLive: boolean;
  winnerTeamId?: string | null;
  isTie: boolean;
  home: typeScoresTeamInput;
  away: typeScoresTeamInput;
  /** ISSUE-1091. The server-built live game state; null unless in progress. */
  liveState?: typeLiveStateWire | null;
  /** ISSUE-1197. The fan has alerts on for this game; only /feed/upcoming sends it. */
  hasAlertsOn?: boolean;
  /** ISSUE-1381. A scheduled MLB game's announced starters, each with the
   *  server's season record "12-8" and ERA "5.19" when known; null otherwise. */
  startingPitchers?: {
    away?: typeStartingPitcherInput | null;
    home?: typeStartingPitcherInput | null;
  } | null;
  /** ISSUE-1392. A scheduled game's server-built line context; null otherwise. */
  pregameLine?: { label: string; spokenLabel: string } | null;
};

/** ISSUE-1381. One announced starter as the list reads send it. */
export type typeStartingPitcherInput = {
  name: string;
  record?: string | null;
  era?: string | null;
};

/** ISSUE-1381. What an unannounced starter reads as on a score row. */
export const STARTING_PITCHER_TBD = "TBD";

/** ISSUE-1381. "MIL: Gasser (12-8, 5.19)", after v1's score row: the
 *  server's record and ERA in parentheses, only the parts it sent; "MIL:
 *  Gasser" with neither, "MIL: TBD" with no starter. */
export function startingPitcherLine(
  plateText: string,
  starter: typeStartingPitcherInput | null | undefined,
): string {
  if (starter == null) return `${plateText}: ${STARTING_PITCHER_TBD}`;
  const parts = [starter.record, starter.era]
    .map((part) => part?.trim() ?? "")
    .filter((part) => part !== "");
  return parts.length === 0
    ? `${plateText}: ${starter.name}`
    : `${plateText}: ${starter.name} (${parts.join(", ")})`;
}

export type typeScoreTeamLine = {
  teamId: string;
  /** The full name. Always the accessible name, whatever label shows. */
  name: string;
  shortName: string | null;
  abbreviation: string;
  /** What the initials plate draws. Equals `abbreviation` unless the other
   *  team in this same game plates identically (ISSUE-902 R4). */
  plateText: string;
  /** The resolved mark URL the server sent, or null when the plate renders. */
  logoUrl: string | null;
  /** The team's color for the initials plate, or null for `primarySolid`. */
  plateColorHex: string | null;
  /** "" when the server sent no score. Never "0" for a game not yet played. */
  scoreLabel: string;
  /** win and lose only on a final with a known winner (section 1.5). */
  emphasis: "win" | "lose" | "neutral";
  /** ISSUE-1091. True only while in play and the server names this team as
   *  the one with the ball. */
  hasPossession: boolean;
  /** ISSUE-1393. The server's "PP" while in play and this hockey team is on a
   *  power play; null otherwise. */
  powerPlayLabel: string | null;
  /** The fan follows this team: the inline star and ", your team". */
  isFavorite: boolean;
  /** ISSUE-1388. The server's poll rank ("5") drawn before the name, or null
   *  for an unranked team. */
  pollRankLabel: string | null;
};

/** ISSUE-1388. The name as it is spoken: "No. 5 Miami" for a ranked team,
 *  the plain name otherwise. */
export function spokenTeamName(
  line: Pick<typeScoreTeamLine, "name" | "pollRankLabel">,
): string {
  return line.pollRankLabel === null
    ? line.name
    : `No. ${line.pollRankLabel} ${line.name}`;
}

/** Which color and leading mark the status label gets (section 1.7). */
export type typeScoreRowStatusTone = "live" | "delayed" | "final" | "muted";

export type typeScoreRowModel = {
  eventId: string;
  /** Away is the FIRST line, home the second. */
  away: typeScoreTeamLine;
  home: typeScoreTeamLine;
  /** False for scheduled, postponed and canceled rows: no score cell at all,
   *  and the name takes its room. */
  hasScore: boolean;
  /** The server's statusLabel, verbatim. */
  stateLabel: string;
  statusTone: typeScoreRowStatusTone;
  /** The server's own isLive flag, never inferred. */
  isLive: boolean;
  /** Live AND in progress. A delayed game is isLive but not in play. */
  isInPlay: boolean;
  /** The whole row's accessible name (section 1.8). */
  ariaLabel: string;
  /** ISSUE-1091. The event's live state while in play; null otherwise. */
  liveState: typeLiveStateWire | null;
  /** ISSUE-1381. "MIL: Gasser (12-8, 5.19)" then the home line, away first,
   *  under a scheduled MLB game whose feed named a starter; null otherwise. */
  startingPitcherLines: [string, string] | null;
  /** ISSUE-1392. "PIT -3.5 · Total 45.5" under a scheduled game with a
   *  stored line, and its spoken sentence, both the server's verbatim; null
   *  otherwise. */
  pregameLine: { label: string; spokenLabel: string } | null;
};

const NO_SCORE_STATUSES: ReadonlySet<typeScoresEventInput["status"]> = new Set([
  "scheduled",
  "postponed",
  "canceled",
]);

function scoreLabelFor(score: number | null | undefined): string {
  return score === null || score === undefined ? "" : String(score);
}

function emphasisFor(input: {
  status: typeScoresEventInput["status"];
  isTie: boolean;
  winnerTeamId: string | null | undefined;
  teamId: string;
}): typeScoreTeamLine["emphasis"] {
  if (input.status !== "final" || input.isTie) return "neutral";
  if (
    input.winnerTeamId === null ||
    input.winnerTeamId === undefined ||
    input.winnerTeamId === ""
  ) {
    return "neutral";
  }
  return input.winnerTeamId === input.teamId ? "win" : "lose";
}

function statusToneFor(
  event: typeScoresEventInput,
  isInPlay: boolean,
): typeScoreRowStatusTone {
  if (isInPlay) return "live";
  if (event.status === "delayed") return "delayed";
  if (event.status === "final") return "final";
  return "muted";
}

function ariaLabelFor(input: {
  event: typeScoresEventInput;
  away: typeScoreTeamLine;
  home: typeScoreTeamLine;
  hasScore: boolean;
  isInPlay: boolean;
  liveState: typeLiveStateWire | null;
}): string {
  const { away, home } = input;
  const awayName = spokenTeamName(away);
  const homeName = spokenTeamName(home);
  const teams =
    input.hasScore && away.scoreLabel !== "" && home.scoreLabel !== ""
      ? `${awayName} ${away.scoreLabel}, ${homeName} ${home.scoreLabel}`
      : `${awayName} at ${homeName}`;
  const parts = [teams, input.event.statusLabel];
  if (input.isInPlay) parts.push("live");
  if (input.liveState !== null && input.liveState.summaryLabel !== "") {
    parts.push(input.liveState.summaryLabel);
  }
  if (away.isFavorite || home.isFavorite) parts.push("your team");
  return parts.join(", ");
}

export function toScoreRowModel(
  event: typeScoresEventInput,
  options: { favoriteTeamIds?: readonly string[] | undefined } = {},
): typeScoreRowModel {
  const isInPlay = event.isLive && event.status === "inProgress";
  const liveState = isInPlay ? (event.liveState ?? null) : null;
  const possessionTeamId = liveState?.possessionTeamId ?? null;
  const hockey = liveState?.hockey ?? null;
  const favorites = new Set(options.favoriteTeamIds ?? []);
  const { awayText, homeText } = resolvePlatePair({
    away: { name: event.away.name, abbreviation: event.away.abbreviation },
    home: { name: event.home.name, abbreviation: event.home.abbreviation },
  });
  const hasScore =
    !NO_SCORE_STATUSES.has(event.status) &&
    (scoreLabelFor(event.away.score) !== "" ||
      scoreLabelFor(event.home.score) !== "");

  const lineFor = (
    team: typeScoresTeamInput,
    plateText: string,
  ): typeScoreTeamLine => ({
    teamId: team.teamId,
    name: team.name,
    shortName:
      team.shortName === undefined || team.shortName === ""
        ? null
        : team.shortName,
    abbreviation: team.abbreviation,
    plateText,
    logoUrl: team.logoUrl ?? null,
    plateColorHex: team.primaryColorHex ?? null,
    scoreLabel: hasScore ? scoreLabelFor(team.score) : "",
    emphasis: emphasisFor({
      status: event.status,
      isTie: event.isTie,
      winnerTeamId: event.winnerTeamId,
      teamId: team.teamId,
    }),
    hasPossession:
      possessionTeamId !== null && possessionTeamId === team.teamId,
    powerPlayLabel:
      hockey !== null && hockey.powerPlayTeamId === team.teamId
        ? hockey.powerPlayLabel
        : null,
    isFavorite: favorites.has(team.teamId),
    pollRankLabel:
      team.pollRankLabel === undefined || team.pollRankLabel === ""
        ? null
        : team.pollRankLabel,
  });

  const away = lineFor(event.away, awayText);
  const home = lineFor(event.home, homeText);
  const starters =
    event.status === "scheduled" ? (event.startingPitchers ?? null) : null;
  const startingPitcherLines: [string, string] | null =
    starters === null || (starters.away == null && starters.home == null)
      ? null
      : [
          startingPitcherLine(awayText, starters.away),
          startingPitcherLine(homeText, starters.home),
        ];

  const pregameLine = event.pregameLine ?? null;

  return {
    eventId: event.eventId,
    away,
    home,
    hasScore,
    stateLabel: event.statusLabel,
    statusTone: statusToneFor(event, isInPlay),
    isLive: event.isLive,
    isInPlay,
    ariaLabel: [
      ariaLabelFor({ event, away, home, hasScore, isInPlay, liveState }),
      ...(startingPitcherLines === null
        ? []
        : [`starting pitchers ${startingPitcherLines.join(", ")}`]),
      ...(pregameLine === null ? [] : [pregameLine.spokenLabel]),
    ].join(", "),
    liveState,
    startingPitcherLines,
    pregameLine,
  };
}
